import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Pencil, X } from "lucide-react";
import api, { formatApiError, fileUrl } from "@/lib/api";

const SECTIONS = [
  // Nouveaux musées thématiques (5)
  { key: "phototheque", label: "Photothèque", needImage: true },
  { key: "presse_ecrite", label: "Musée presse écrite", needImage: true, needFile: true },
  { key: "audiovisuel", label: "Musée audiovisuel", needImage: true, needFile: true },
  { key: "cinema", label: "Musée cinéma", needImage: true, needFile: true },
  { key: "tic", label: "Musée TIC RDC", needImage: true, needFile: true },
  // Anciennes sections (compat rétro)
  { key: "photos", label: "Archives photos (hérité)", needImage: true },
  { key: "publications", label: "Archives publications (hérité)", needImage: false, needFile: true },
  { key: "evenements", label: "Archives Événements (hérité)", needImage: true, needDate: true },
];

const MEDIA_TYPES = [
  { value: "image", label: "Image / photo" },
  { value: "video", label: "Vidéo" },
  { value: "audio", label: "Audio" },
  { value: "pdf", label: "Document PDF" },
];

const EMPTY = { title: "", description: "", media_type: "image", image_url: "", file_path: "", video_url: "", audio_url: "", collection: "", event_date: "", order: 0 };

export default function MuseumAdmin() {
  const [section, setSection] = useState("photos");
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [uploadingImg, setUploadingImg] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const imgRef = useRef();
  const fileRef = useRef();

  const cfg = SECTIONS.find((s) => s.key === section);

  const load = () => api.get(`/museum/${section}`).then((r) => setItems(r.data));
  useEffect(() => { load(); resetForm(); /* eslint-disable-next-line */ }, [section]);

  const resetForm = () => {
    setForm(EMPTY); setEditingId(null);
    if (imgRef.current) imgRef.current.value = "";
    if (fileRef.current) fileRef.current.value = "";
  };

  const upload = async (file, kind, setter) => {
    const fd = new FormData(); fd.append("file", file); fd.append("kind", kind);
    setter(true);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return data.path;
    } finally { setter(false); }
  };
  const onPickImg = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const p = await upload(f, "museum-img", setUploadingImg); setForm((v) => ({ ...v, image_url: fileUrl(p) })); }
    catch (err) { setError(formatApiError(err)); }
  };
  const onPickFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const p = await upload(f, "museum-file", setUploadingFile); setForm((v) => ({ ...v, file_path: p })); }
    catch (err) { setError(formatApiError(err)); }
  };

  const submit = async (e) => {
    e.preventDefault(); setError("");
    try {
      if (editingId) {
        await api.patch(`/museum/item/${editingId}`, form);
      } else {
        await api.post(`/museum/${section}`, form);
      }
      resetForm(); load();
    } catch (err) { setError(formatApiError(err)); }
  };
  const edit = (it) => { setForm({ ...EMPTY, ...it }); setEditingId(it.id); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const del = async (id) => {
    if (!window.confirm("Supprimer cet élément ?")) return;
    await api.delete(`/museum/item/${id}`); load();
  };

  return (
    <div data-testid="museum-admin">
      <div className="eyebrow eyebrow-dark">Musées</div>
      <h1 className="mt-2 text-3xl font-bold text-unisic-blue">Galeries et archives des musées</h1>
      <p className="mt-2 text-slate-600 text-sm">Gérez le contenu des trois sections du musée numérique de l'UNISIC.</p>
      <span className="divider-gold mt-3" />

      <div className="mt-6 flex gap-2 flex-wrap" data-testid="museum-tabs">
        {SECTIONS.map((s) => (
          <button
            key={s.key}
            onClick={() => setSection(s.key)}
            className={`px-4 py-2 text-sm rounded-sm border ${section === s.key ? "bg-unisic-blue text-white border-unisic-blue" : "bg-white border-slate-300 text-slate-700 hover:border-unisic-blue"}`}
            data-testid={`tab-museum-${s.key}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="mt-6 paper-card p-6 grid gap-4" data-testid="museum-form">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-unisic-blue">
            {editingId ? "Modifier l'élément" : `Ajouter dans « ${cfg.label} »`}
          </div>
          {editingId && (
            <button type="button" onClick={resetForm} className="text-xs text-slate-500 hover:text-unisic-red flex items-center gap-1">
              <X className="w-3 h-3" /> Annuler
            </button>
          )}
        </div>
        <input required placeholder="Titre" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="border border-slate-300 px-3 py-2.5 rounded-sm text-sm" data-testid="mus-title" />
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">Type de média</label>
            <select value={form.media_type} onChange={(e) => setForm({ ...form, media_type: e.target.value })} className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm bg-white" data-testid="mus-media-type">
              {MEDIA_TYPES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">Collection (regroupement)</label>
            <input placeholder="Ex : Années 1960, Émissions phares..." value={form.collection} onChange={(e) => setForm({ ...form, collection: e.target.value })} className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" data-testid="mus-collection" />
          </div>
        </div>
        <textarea rows={3} placeholder="Description (facultatif)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="border border-slate-300 px-3 py-2.5 rounded-sm text-sm" />
        {form.media_type === "video" && (
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">URL vidéo externe (YouTube/Vimeo, facultatif si upload)</label>
            <input placeholder="https://..." value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" data-testid="mus-video-url" />
          </div>
        )}
        {form.media_type === "audio" && (
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">URL audio externe (facultatif si upload)</label>
            <input placeholder="https://..." value={form.audio_url} onChange={(e) => setForm({ ...form, audio_url: e.target.value })} className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" data-testid="mus-audio-url" />
          </div>
        )}
        {cfg.needDate && (
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">Date de l'événement</label>
            <input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" data-testid="mus-date" />
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">Image {cfg.needImage ? "(couverture / miniature)" : "(facultatif)"}</label>
            <div className="mt-1 flex items-center gap-3">
              <input ref={imgRef} type="file" accept="image/*" onChange={onPickImg} className="text-xs" data-testid="mus-image" />
              {uploadingImg && <span className="text-xs text-slate-500">Envoi…</span>}
              {form.image_url && !uploadingImg && <img src={form.image_url} alt="" className="w-10 h-10 object-cover rounded-sm" />}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">Fichier média {form.media_type === "image" ? "" : `(${form.media_type})`}</label>
            <div className="mt-1 flex items-center gap-3">
              <input ref={fileRef} type="file" onChange={onPickFile} className="text-xs" data-testid="mus-file" />
              {uploadingFile && <span className="text-xs text-slate-500">Envoi…</span>}
              {form.file_path && !uploadingFile && <span className="text-xs text-green-700">Envoyé</span>}
            </div>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">Ordre d'affichage (0 = premier)</label>
          <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || "0", 10) })} className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" />
        </div>
        {error && <div className="text-sm text-unisic-red" data-testid="mus-error">{error}</div>}
        <div className="flex justify-end">
          <button className="btn-primary text-sm" type="submit" data-testid="btn-save-mus"><Plus className="w-4 h-4" /> {editingId ? "Enregistrer" : "Ajouter"}</button>
        </div>
      </form>

      <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {items.map((it) => (
          <div key={it.id} className="paper-card p-4" data-testid={`mus-row-${it.id}`}>
            {it.image_url && <img src={it.image_url} alt="" className="w-full h-40 object-cover rounded-sm mb-3" />}
            <h3 className="font-display font-semibold text-unisic-blue truncate">{it.title}</h3>
            {it.event_date && <div className="text-[11px] uppercase tracking-widest text-unisic-gold mt-1">{new Date(it.event_date).toLocaleDateString("fr-FR")}</div>}
            {it.description && <p className="mt-2 text-xs text-slate-600 line-clamp-3">{it.description}</p>}
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={() => edit(it)} className="text-slate-400 hover:text-unisic-blue" data-testid={`btn-edit-mus-${it.id}`}><Pencil className="w-4 h-4" /></button>
              <button onClick={() => del(it.id)} className="text-slate-400 hover:text-unisic-red" data-testid={`btn-del-mus-${it.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full text-sm text-slate-500 text-center py-10">Aucun élément dans cette section.</div>}
      </div>
    </div>
  );
}
